import { AUDIO_CHUNK_DURATION } from './constants';
import logger from './logger';
import {
  checkIndexedDBHealth,
  getLastChunkIndex,
  getMeetingMetadata,
  saveChunk,
  saveMeetingMetadata
} from './recordingDB';

class AudioRecorderService {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunkIndex = 0;
  private meetingName: string | null = null;
  private sessionId: string | null = null;
  private startTime: number | null = null;
  private pendingSaves: Promise<any>[] = [];
  private pausedDuration = 0;
  private pauseStartTime: number | null = null;
  private existingDuration = 0;
  private isStopped = false;

  private cleanupStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.stream = null;
    }
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private async setupRecorder() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    if (this.isStopped) {
      stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      this.recorder = null;
      return;
    }

    this.stream = stream;
    this.recorder = new MediaRecorder(this.stream, {
      mimeType: 'audio/webm; codecs=opus'
    });

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.meetingName) {
        const savePromise = saveChunk(
          this.meetingName,
          this.chunkIndex,
          event.data,
          this.sessionId
        );
        this.pendingSaves.push(savePromise);

        this.chunkIndex++;

        savePromise.then(() => {
          this.pendingSaves = this.pendingSaves.filter(
            (p) => p !== savePromise
          );
        });
      }
    };

    this.recorder.start(AUDIO_CHUNK_DURATION);
  }

  async start(meetingName: string) {
    const isDBReady = await checkIndexedDBHealth();
    if (!isDBReady) {
      throw new Error('INDEXEDDB_UNAVAILABLE');
    }

    this.isStopped = false;
    this.meetingName = meetingName;
    this.chunkIndex = 0;
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.pausedDuration = 0;
    this.pauseStartTime = null;
    this.existingDuration = 0;

    await this.setupRecorder();
  }

  async resumeExistingRecording(meetingName: string) {
    const isDBReady = await checkIndexedDBHealth();
    if (!isDBReady) {
      throw new Error('INDEXEDDB_UNAVAILABLE');
    }

    this.isStopped = false;
    this.meetingName = meetingName;

    this.chunkIndex = await getLastChunkIndex(meetingName);
    this.sessionId = this.generateSessionId();

    const existingMeta = await getMeetingMetadata(meetingName);
    this.existingDuration =
      existingMeta?.duration ?? this.chunkIndex * AUDIO_CHUNK_DURATION;

    this.startTime = Date.now();
    this.pausedDuration = 0;
    this.pauseStartTime = null;

    await this.setupRecorder();
  }

  async stop(): Promise<{ meetingName: string | null }> {
    this.isStopped = true;

    if (!this.recorder || this.recorder.state === 'inactive') {
      this.cleanupStream();
      return { meetingName: this.meetingName };
    }

    if (this.pauseStartTime !== null) {
      this.pausedDuration += Date.now() - this.pauseStartTime;
      this.pauseStartTime = null;
    }

    const newlyRecorded = Date.now() - (this.startTime || Date.now());
    const duration = Math.max(
      0,
      this.existingDuration + newlyRecorded - this.pausedDuration
    );

    return new Promise((resolve) => {
      this.recorder!.onstop = async () => {
        try {
          await Promise.all(this.pendingSaves);

          if (this.meetingName) {
            await saveMeetingMetadata(this.meetingName, { duration });
          }
        } catch (error) {
          logger.error('Error during stop cleanup:', error);
        } finally {
          this.cleanupStream();
          this.recorder = null;
          this.sessionId = null;
          this.pausedDuration = 0;
          this.pauseStartTime = null;
          this.existingDuration = 0;
          resolve({ meetingName: this.meetingName });
        }
      };

      this.recorder!.stop();
    });
  }

  discard() {
    this.isStopped = true;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.ondataavailable = null;
        this.recorder.onstop = null;
        this.recorder.stop();
      }
    } catch (err) {
      logger.error('Error stopping recorder during discard:', err);
    }
    this.cleanupStream();
    this.recorder = null;
    this.sessionId = null;
    this.meetingName = null;
    this.chunkIndex = 0;
    this.startTime = null;
    this.pendingSaves = [];
    this.pausedDuration = 0;
    this.pauseStartTime = null;
    this.existingDuration = 0;
  }

  pause() {
    if (this.recorder?.state === 'recording') {
      this.pauseStartTime = Date.now();
      this.recorder.pause();
    }
  }

  resume() {
    if (this.recorder?.state === 'paused') {
      if (this.pauseStartTime !== null) {
        this.pausedDuration += Date.now() - this.pauseStartTime;
        this.pauseStartTime = null;
      }
      this.recorder.resume();
    }
  }

  getState(): string {
    return this.recorder?.state || 'inactive';
  }

  getStream(): MediaStream | null {
    return this.stream;
  }
}

const audioRecorderService = new AudioRecorderService();
export default audioRecorderService;
